import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OriginSummary } from "../../lib/origin.jsx";
import {
  Wallet, DollarSign, Cpu, Cloud, Sparkles, RefreshCcw, Download, TrendingUp,
  TrendingDown, Minus, Save, AlertTriangle, Loader2, Info, Edit3, Database,
  History, ChevronRight, X, PlayCircle, ArrowLeftCircle,
} from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Line,
} from "recharts";
import { Tile, Badge, SectionHeader, StatusDot, Sparkline } from "../../components/Primitives";
import { useChartColors } from "../../lib/chartColors";
import { useRole } from "../../lib/role.jsx";
import {
  costOverview, costTopConsumers, costRateCard,
  costRateCardHistory, costRateCardVersion,
  updateCostRateCard, downloadCostCsv, downloadCostXlsx, downloadRateCardXlsx, fmtUsd,
} from "../../lib/costApi";
import { cn, formatNumber } from "../../lib/utils";

// -----------------------------------------------------------------------------
// Cost Transparency Dashboard
//   • USD-only (the only USD screen in 1Rotary; rest of the app stays in Peso)
//   • Overview tab: hero strip + 3 category cards + trend chart + top-10 lists
//   • Rate card tab: IT Head only. All other allowed roles see read-only values.
// -----------------------------------------------------------------------------

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg min-w-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular text-[11px]">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted capitalize">{(p.name || p.dataKey || "").replace("_", " ")}</span>
          <span className="ml-auto font-semibold">{p.value == null ? "n/a" : fmtUsd(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function CostTransparency() {
  const { user, role } = useRole();
  const canEdit = role?.id === "it_head";

  const [tab, setTab] = useState("overview");
  // Replay = view the Overview tab as if a historical rate card were still in effect.
  // Shape: { versionId, createdAt, actor } | null. Set from the Rate card tab's history list.
  const [replay, setReplay] = useState(null);

  const activateReplay = (v) => {
    setReplay({ versionId: v.version_id, createdAt: v.created_at, actor: v.actor });
    setTab("overview");
  };
  const clearReplay = () => setReplay(null);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Admin Console · Financial Transparency"
        title="Cost Transparency"
        description="The only USD screen in 1Rotary — month-to-date spend, end-of-month forecast and per-service breakdown driven by the platform's self-metered usage. Hard figures only: unmeasured days stay unmeasured."
        action={
          <div className="flex items-center gap-2" data-testid="cost-tabs">
            <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="cost-tab-overview" label="Overview" />
            <TabBtn active={tab === "rate_card"} onClick={() => setTab("rate_card")} testid="cost-tab-rate-card" label="Rate card" />
          </div>
        }
      />

      <OriginSummary variant="system" />

      {tab === "overview"
        ? <OverviewTab actor={user?.employeeId} canEdit={canEdit}
                       replay={replay} onClearReplay={clearReplay} />
        : <RateCardTab actor={user?.employeeId} canEdit={canEdit}
                       onActivateReplay={activateReplay} />}
    </div>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={cn("px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors border",
              active ? "border-primary bg-primary/10 text-primary"
                     : "border-border text-muted hover:text-fg hover:border-primary/60")}>
      {label}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Overview tab
// -----------------------------------------------------------------------------
function OverviewTab({ actor, canEdit, replay, onClearReplay }) {
  const C = useChartColors();
  const [overview, setOverview] = useState(null);
  const [consumers, setConsumers] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // null | "csv" | "xlsx"
  const [drill, setDrill] = useState(null); // null | "cloud" | "compute" | "ai_tokens"
  const [error, setError] = useState(null);

  const versionId = replay?.versionId || undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const [ov, cons] = await Promise.all([
        costOverview(days, { versionId }),
        costTopConsumers(10, { versionId }),
      ]);
      setOverview(ov);
      setConsumers(cons);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, versionId]);
  useEffect(() => { load(); }, [load]);

  async function onCsv() {
    setDownloading("csv");
    try { await downloadCostCsv(90); } catch (e) { setError(e.message); }
    finally { setDownloading(null); }
  }
  async function onXlsx() {
    setDownloading("xlsx");
    try { await downloadCostXlsx(90, { versionId }); } catch (e) { setError(e.message); }
    finally { setDownloading(null); }
  }

  // Pretty 7-day MA + forecast overlay built from measured avg per category.
  const chartData = useMemo(() => overview ? buildChartData(overview, days) : [], [overview, days]);

  if (loading && !overview) return <SkeletonOverview />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!overview) return null;

  const h = overview.hero;
  const cats = overview.categories;
  const series = overview.daily_series;

  return (
    <>
      {replay && (
        <div data-testid="replay-banner"
             className="border border-warning/40 bg-warning/5 text-warning rounded-sm p-3 flex items-center gap-3">
          <PlayCircle size={16} className="shrink-0" />
          <div className="flex-1 min-w-0 text-[12px]">
            <div className="font-semibold">
              Replaying the Overview at the rate card from{" "}
              <span className="font-mono-tabular">{(replay.createdAt || "").slice(0, 19).replace("T", " ")} UTC</span>
            </div>
            <div className="text-[11px] opacity-90">
              Set by <strong className="font-mono-tabular">{replay.actor || "unknown"}</strong>.
              Every USD figure, drill-down and the Excel export use that historical card.
              Current meter data is unchanged.
            </div>
          </div>
          <button data-testid="replay-clear" onClick={onClearReplay}
                  className="shrink-0 px-3 py-1.5 text-[11px] border border-warning/60 hover:bg-warning hover:text-primary-fg rounded-sm inline-flex items-center gap-1">
            <ArrowLeftCircle size={11} /> Back to current rates
          </button>
        </div>
      )}

      {/* Hero strip */}
      <div className="grid grid-cols-12 gap-4">
        <HeroStat testid="hero-mtd"
                  label="Month-to-date spend"
                  value={fmtUsd(h.mtd_total, { compact: true })}
                  sub={<>of forecast <strong className="text-fg">{fmtUsd(h.forecast_total, { compact: true })}</strong>
                       {h.mtd_vs_forecast_pct != null && <> &middot; {h.mtd_vs_forecast_pct}% through</>}</>}
                  tone="primary" icon={DollarSign} />
        <HeroStat testid="hero-forecast"
                  label="Forecasted month-end"
                  value={fmtUsd(h.forecast_total, { compact: true })}
                  sub={<>Confidence <ConfidencePill tier={h.confidence} /> &middot; {overview.month.days_elapsed}/{overview.month.days_in_month} days measured</>}
                  tone="info" icon={Sparkles} />
        <HeroStat testid="hero-yesterday"
                  label="Yesterday's spend"
                  value={h.yesterday_total == null ? "n/a" : fmtUsd(h.yesterday_total, { compact: true })}
                  sub={<DeltaBadge pct={h.yesterday_delta_pct} />}
                  tone="success" icon={TrendingUp} />
      </div>

      {/* Three category cards */}
      <div className="grid grid-cols-12 gap-4">
        <CategoryCard testid="cat-cloud"       title="Cloud"      icon={Cloud}
                      mtd={cats.cloud.mtd}      forecast={cats.cloud.forecast}
                      sparkline={cats.cloud.sparkline} tone="info"
                      hint="S3 storage, requests and egress — driven by the S3 audit trail + Bronze catalog."
                      drillOpen={drill === "cloud"} onDrill={() => setDrill(d => d === "cloud" ? null : "cloud")} />
        <CategoryCard testid="cat-compute"     title="Compute"    icon={Cpu}
                      mtd={cats.compute.mtd}    forecast={cats.compute.forecast}
                      sparkline={cats.compute.sparkline} tone="warning"
                      hint="Daily engine cycle + S3 health scheduler + background jobs — duration × $/vCPU-hour."
                      drillOpen={drill === "compute"} onDrill={() => setDrill(d => d === "compute" ? null : "compute")} />
        <CategoryCard testid="cat-ai-tokens"   title="AI Tokens"  icon={Sparkles}
                      mtd={cats.ai_tokens.mtd}  forecast={cats.ai_tokens.forecast}
                      sparkline={cats.ai_tokens.sparkline} tone="primary"
                      hint="Every LLM call routed through the platform — logged per feature, model and actor."
                      drillOpen={drill === "ai_tokens"} onDrill={() => setDrill(d => d === "ai_tokens" ? null : "ai_tokens")} />
      </div>

      {/* Drill panels */}
      {drill === "cloud" && (
        <DrillPanel testid="drill-cloud" title="Cloud · Top S3 buckets · month-to-date"
                    columns={["Bucket / prefix", "Requests", "Egress GB", "Stored GB", "$/day", "MTD $", "Forecast $"]}
                    rows={(consumers?.cloud_buckets || []).map(r => [
                      r.bucket, formatNumber(r.requests || 0),
                      ((r.bytes_out || 0) / (1024 ** 3)).toFixed(2),
                      r.stored_gb != null ? r.stored_gb.toFixed(2) : "—",
                      fmtUsd(r.daily_usd), fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                    ])}
                    empty="No S3 operations logged this month yet." />
      )}
      {drill === "compute" && (
        <DrillPanel testid="drill-compute" title="Compute · Top jobs · month-to-date"
                    columns={["Job", "Runs", "Minutes", "$/day", "MTD $", "Forecast $"]}
                    rows={(consumers?.compute_jobs || []).map(r => [
                      r.job, formatNumber(r.runs),
                      formatNumber(r.duration_minutes, { decimals: 2 }),
                      fmtUsd(r.daily_usd), fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                    ])}
                    empty="No scheduled jobs have run this month yet." />
      )}
      {drill === "ai_tokens" && (
        <DrillPanel testid="drill-ai-tokens" title="AI Tokens · Top features · month-to-date"
                    columns={["Feature", "Model", "Calls", "In tokens", "Out tokens", "$/day", "MTD $", "Forecast $"]}
                    rows={(consumers?.ai_tokens || []).map(r => [
                      r.feature, r.model, formatNumber(r.calls),
                      formatNumber(r.input_tokens), formatNumber(r.output_tokens),
                      fmtUsd(r.daily_usd), fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                    ])}
                    empty="No LLM calls recorded this month yet. Try asking the Copilot a question." />
      )}

      {/* Trend chart */}
      <Tile testid="trend-chart" title={`Daily spend · last ${days} days`}
            action={
              <div className="flex items-center gap-2">
                <select data-testid="trend-days" value={days} onChange={e => setDays(parseInt(e.target.value, 10))}
                        className="bg-surface-2 border border-border text-[11px] px-2 py-1 rounded-sm font-mono-tabular">
                  {[14, 30, 60, 90].map(d => <option key={d} value={d}>{d}d</option>)}
                </select>
                <button data-testid="cost-reload" onClick={load}
                        className="px-2 py-1 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
                  <RefreshCcw size={11} /> Reload
                </button>
                <button data-testid="cost-csv" onClick={onCsv} disabled={downloading === "csv"}
                        className="px-2 py-1 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                  {downloading === "csv" ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} CSV 90d
                </button>
                <button data-testid="cost-xlsx" onClick={onXlsx} disabled={downloading === "xlsx"}
                        className="px-2 py-1 text-[11px] border border-success/60 text-success hover:bg-success hover:text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                  {downloading === "xlsx" ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Excel 90d
                </button>
              </div>
            }>
        <div style={{ height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="g-cloud" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.chart[1]} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={C.chart[1]} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="g-compute" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.chart[2]} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={C.chart[2]} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="g-ai" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.chart[0]} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={C.chart[0]} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="date" tick={tickStyle} stroke={C.borderStrong}
                     tickFormatter={d => d.slice(5)} minTickGap={30} />
              <YAxis tick={tickStyle} stroke={C.borderStrong}
                     tickFormatter={v => `$${v < 1 ? v.toFixed(2) : v}`} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="cloud"     stackId="s" stroke={C.chart[1]} fill="url(#g-cloud)" isAnimationActive={false} />
              <Area type="monotone" dataKey="compute"   stackId="s" stroke={C.chart[2]} fill="url(#g-compute)" isAnimationActive={false} />
              <Area type="monotone" dataKey="ai_tokens" stackId="s" stroke={C.chart[0]} fill="url(#g-ai)" name="AI tokens" isAnimationActive={false} />
              <Line type="monotone" dataKey="forecast" stroke={C.chart[3]} strokeDasharray="4 4" dot={false} isAnimationActive={false} name="Forecast" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[11px] text-muted mt-2 flex items-center flex-wrap gap-x-4 gap-y-1">
          <span><Info size={11} className="inline mr-1" />Days before <strong className="text-fg font-mono-tabular">{overview.meter_floor || "—"}</strong> pre-date the meters and show as <strong className="text-fg">n/a</strong>.</span>
          <span>Dashed line is a forecast built on the 7-day moving average through month-end.</span>
        </div>
      </Tile>

      {/* Top consumers — all three lists always visible so planners can scan what to attack */}
      <div className="grid grid-cols-12 gap-4">
        <TopList testid="top-ai"      title="Top features · AI tokens"
                 columns={["Feature", "Calls", "MTD $", "Forecast $"]}
                 rows={(consumers?.ai_tokens || []).slice(0, 10).map(r => [
                   <span className="font-medium">{r.feature}</span>,
                   formatNumber(r.calls),
                   fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                 ])}
                 empty="No AI-token spend yet this month." />
        <TopList testid="top-cloud"   title="Top S3 buckets · storage + egress"
                 columns={["Bucket", "Requests", "MTD $", "Forecast $"]}
                 rows={(consumers?.cloud_buckets || []).slice(0, 10).map(r => [
                   <span className="font-medium font-mono-tabular text-[11.5px]">{r.bucket}</span>,
                   formatNumber(r.requests || 0),
                   fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                 ])}
                 empty="No S3 operations recorded this month yet." />
        <TopList testid="top-compute" title="Top jobs · compute minutes"
                 columns={["Job", "Min", "MTD $", "Forecast $"]}
                 rows={(consumers?.compute_jobs || []).slice(0, 10).map(r => [
                   <span className="font-medium font-mono-tabular text-[11.5px]">{r.job}</span>,
                   formatNumber(r.duration_minutes, { decimals: 2 }),
                   fmtUsd(r.mtd_usd), fmtUsd(r.forecast_month_usd),
                 ])}
                 empty="No jobs have run yet this month." />
      </div>
    </>
  );
}

function buildChartData(overview, days) {
  const series = overview.daily_series.slice(-days);
  // Forecast line: build a dashed extension by averaging the last-7 measured
  // totals and drawing a flat line across remaining calendar days of month.
  const dim = overview.month.days_in_month;
  const measured = series.filter(r => r.measured);
  const avg = measured.length ? measured.slice(-7).reduce((s, r) => s + (r.total || 0), 0) / Math.min(7, measured.length) : 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const data = series.map(r => ({
    date: r.date,
    cloud: r.measured ? r.cloud : null,
    compute: r.measured ? r.compute : null,
    ai_tokens: r.measured ? r.ai_tokens : null,
    forecast: r.date > todayStr ? avg : null,
  }));
  return data;
}

function HeroStat({ label, value, sub, tone = "primary", icon: Icon, testid }) {
  return (
    <div data-testid={testid}
         className={cn("col-span-12 md:col-span-4 tile p-5 border transition-colors",
           tone === "primary" ? "border-primary/40 bg-primary/5"
           : tone === "info" ? "border-info/40 bg-info/5"
           : "border-success/40 bg-success/5")}>
      <div className="flex items-center gap-2 text-muted">
        <Icon size={13} className={cn(
          tone === "primary" ? "text-primary" :
          tone === "info" ? "text-info" : "text-success"
        )} />
        <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="font-mono-tabular text-4xl md:text-[42px] font-semibold mt-2 leading-none"
           data-testid={`${testid}-value`}>
        {value}
      </div>
      <div className="text-[11px] text-muted mt-3">{sub}</div>
    </div>
  );
}

function ConfidencePill({ tier }) {
  const label = tier === "high" ? "high" : tier === "medium" ? "medium" : "low";
  const status = tier === "high" ? "success" : tier === "medium" ? "info" : "warning";
  return <Badge status={status} testid="confidence-pill">{label}</Badge>;
}

function DeltaBadge({ pct }) {
  if (pct == null) return <span className="text-muted">no prior-day data</span>;
  const up = pct > 0.5;
  const down = pct < -0.5;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const tone = up ? "text-critical" : down ? "text-success" : "text-muted";
  return (
    <span className={cn("inline-flex items-center gap-1 font-mono-tabular", tone)} data-testid="hero-delta">
      <Icon size={11} /> {Math.abs(pct).toFixed(1)}% vs. day-before
    </span>
  );
}

function CategoryCard({ title, icon: Icon, mtd, forecast, sparkline, tone, hint, drillOpen, onDrill, testid }) {
  const colorCls = tone === "info" ? "text-info" : tone === "warning" ? "text-warning" : "text-primary";
  return (
    <button data-testid={testid} onClick={onDrill}
            className={cn("col-span-12 md:col-span-4 tile p-4 text-left transition-colors group",
              drillOpen ? "border-primary bg-primary/5" : "hover:border-primary/60")}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={colorCls} />
        <span className="text-[11px] uppercase tracking-widest font-semibold text-muted">{title}</span>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted">
          {drillOpen ? "Hide drill" : "Tap to drill"}
        </span>
      </div>
      <div className="font-mono-tabular text-3xl font-semibold mt-2" data-testid={`${testid}-mtd`}>
        {fmtUsd(mtd, { compact: true })}
      </div>
      <div className="text-[11px] text-muted mt-0.5">
        MTD · Forecast <strong className="text-fg font-mono-tabular" data-testid={`${testid}-forecast`}>{fmtUsd(forecast, { compact: true })}</strong>
      </div>
      <div className="mt-3">
        <SparklineOrNA data={sparkline} tone={tone} />
      </div>
      <div className="text-[10px] text-muted leading-snug mt-2">{hint}</div>
    </button>
  );
}

function SparklineOrNA({ data, tone }) {
  const measured = (data || []).filter(v => v != null);
  if (measured.length === 0) {
    return <div className="text-[10px] text-muted italic">No daily measurements yet.</div>;
  }
  const color = tone === "info" ? "var(--info)" : tone === "warning" ? "var(--warning)" : "var(--primary)";
  return <Sparkline data={measured} color={color} height={28} />;
}

function TopList({ title, columns, rows, empty, testid }) {
  return (
    <Tile className="col-span-12 lg:col-span-4" title={title} testid={testid}>
      <div className="overflow-x-auto -mx-4 px-4 max-h-[280px] overflow-y-auto">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
              {columns.map((c, i) => (
                <th key={i} className={cn("py-1.5 pr-2", i === 0 ? "text-left" : "text-right")}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="py-6 text-center text-muted italic text-[11px]">{empty}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-surface-2/40 transition-colors">
                {r.map((cell, j) => (
                  <td key={j} className={cn("py-1.5 pr-2", j === 0 ? "text-left" : "text-right font-mono-tabular")}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Tile>
  );
}

function DrillPanel({ title, columns, rows, empty, testid }) {
  return (
    <Tile testid={testid} title={title}
          action={<Badge status="info">{rows.length} rows</Badge>}>
      {rows.length === 0 ? (
        <div className="py-8 text-center text-muted text-[12px] italic">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                {columns.map((c, i) => (
                  <th key={i} className={cn("py-2 px-3", i === 0 ? "text-left" : "text-right")}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-border/60 hover:bg-surface-2/40">
                  {r.map((cell, j) => (
                    <td key={j} className={cn("py-2 px-3", j === 0 ? "text-left" : "text-right font-mono-tabular")}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Tile>
  );
}

// -----------------------------------------------------------------------------
// Rate card tab
// -----------------------------------------------------------------------------
function RateCardTab({ actor, canEdit, onActivateReplay }) {
  const [rc, setRc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [newModelId, setNewModelId] = useState("");
  const [history, setHistory] = useState(null); // null = not loaded, [] = loaded empty
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await costRateCard();
      setRc(d);
      setDraft(deepClone(d));
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { versions } = await costRateCardHistory(50);
      setHistory(versions || []);
    } catch (e) { setError(e.message); }
    finally { setHistoryLoading(false); }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  async function onXlsx() {
    setDownloading(true);
    try { await downloadRateCardXlsx(); } catch (e) { setError(e.message); }
    finally { setDownloading(false); }
  }

  if (!draft) return <SkeletonCard />;

  function setField(k, v) { setDraft(d => ({ ...d, [k]: v })); }
  function setModel(id, patch) {
    setDraft(d => ({
      ...d,
      llm_models: { ...d.llm_models, [id]: { ...(d.llm_models?.[id] || {}), ...patch } },
    }));
  }
  function addModel() {
    const id = newModelId.trim();
    if (!id) return;
    if (draft.llm_models?.[id]) { setError(`Model "${id}" already exists.`); return; }
    setDraft(d => ({
      ...d,
      llm_models: { ...d.llm_models, [id]: { label: id, input_per_m_tokens: 0, output_per_m_tokens: 0 } },
    }));
    setNewModelId("");
  }
  async function onSave() {
    setSaving(true);
    try {
      setError(null); setSaved(null);
      const patch = {
        s3_standard_gb_month: parseFloat(draft.s3_standard_gb_month),
        s3_put_per_1k_requests: parseFloat(draft.s3_put_per_1k_requests),
        s3_get_per_1k_requests: parseFloat(draft.s3_get_per_1k_requests),
        s3_egress_per_gb: parseFloat(draft.s3_egress_per_gb),
        vcpu_hour: parseFloat(draft.vcpu_hour),
        llm_models: Object.fromEntries(
          Object.entries(draft.llm_models || {}).map(([k, v]) => [k, {
            label: v.label || k,
            input_per_m_tokens:  parseFloat(v.input_per_m_tokens),
            output_per_m_tokens: parseFloat(v.output_per_m_tokens),
          }]),
        ),
      };
      const r = await updateCostRateCard(patch, { actor, actorRole: "it_head" });
      setRc(r); setDraft(deepClone(r)); setSaved(`Saved · ${(r.updated_at || "").slice(0, 16).replace("T", " ")}`);
      await loadHistory();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }
  function onReset() { setDraft(deepClone(rc)); setSaved(null); }

  const dirty = JSON.stringify(draft) !== JSON.stringify(rc);
  const inputCls = cn("w-full bg-surface-2 border border-border rounded-sm px-2.5 py-1.5 text-[12px] font-mono-tabular text-right",
                       "focus:border-primary outline-none",
                       !canEdit && "opacity-70 cursor-not-allowed");

  return (
    <>
      {!canEdit && (
        <div className="border border-info/40 bg-info/5 text-info rounded-sm p-3 text-[12px]"
             data-testid="rate-card-readonly-banner">
          <Info size={13} className="inline mr-1" />
          Rate-card edits are restricted to the IT Head role. You can view the current rates below.
        </div>
      )}
      {error && (
        <div data-testid="rate-card-error" className="border border-critical/40 bg-critical/5 text-critical rounded-sm p-3 text-[12px]">
          <AlertTriangle size={13} className="inline mr-1" /> {error}
        </div>
      )}

      <Tile testid="rate-card-s3" title="Amazon S3" action={<Edit3 size={12} className="text-muted" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RateInput label="Standard storage" unit="$/GB-month" testid="rc-s3-standard"
                     value={draft.s3_standard_gb_month} readOnly={!canEdit}
                     onChange={v => setField("s3_standard_gb_month", v)} inputCls={inputCls} />
          <RateInput label="Egress" unit="$/GB" testid="rc-s3-egress"
                     value={draft.s3_egress_per_gb} readOnly={!canEdit}
                     onChange={v => setField("s3_egress_per_gb", v)} inputCls={inputCls} />
          <RateInput label="PUT requests" unit="$/1K requests" testid="rc-s3-put"
                     value={draft.s3_put_per_1k_requests} readOnly={!canEdit}
                     onChange={v => setField("s3_put_per_1k_requests", v)} inputCls={inputCls} />
          <RateInput label="GET / LIST / HEAD requests" unit="$/1K requests" testid="rc-s3-get"
                     value={draft.s3_get_per_1k_requests} readOnly={!canEdit}
                     onChange={v => setField("s3_get_per_1k_requests", v)} inputCls={inputCls} />
        </div>
      </Tile>

      <Tile testid="rate-card-compute" title="Compute" action={<Edit3 size={12} className="text-muted" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RateInput label="vCPU-hour rate" unit="$/vCPU-hour" testid="rc-vcpu"
                     value={draft.vcpu_hour} readOnly={!canEdit}
                     onChange={v => setField("vcpu_hour", v)} inputCls={inputCls} />
        </div>
        <div className="text-[11px] text-muted mt-2 leading-relaxed">
          <Info size={11} className="inline mr-1" />
          Compute cost is approximated as <code>job-duration × $/vCPU-hour</code>. The preview pod does not expose
          Kubernetes CPU metrics to the application, so bend this rate until it matches your real monthly invoice.
        </div>
      </Tile>

      <Tile testid="rate-card-llm" title="LLM models" action={<Edit3 size={12} className="text-muted" />}>
        <div className="space-y-3">
          {Object.entries(draft.llm_models || {}).map(([id, m]) => (
            <div key={id} data-testid={`rc-llm-${id}`}
                 className="border border-border rounded-sm p-3 bg-surface-2/40">
              <div className="flex items-center gap-2 mb-2">
                <Badge status="info" testid={`rc-llm-${id}-label`}>{m.label || id}</Badge>
                <span className="text-[10.5px] text-muted font-mono-tabular ml-auto">{id}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <RateInput label="Input" unit="$/M tokens" testid={`rc-llm-${id}-in`}
                           value={m.input_per_m_tokens} readOnly={!canEdit}
                           onChange={v => setModel(id, { input_per_m_tokens: v })} inputCls={inputCls} />
                <RateInput label="Output" unit="$/M tokens" testid={`rc-llm-${id}-out`}
                           value={m.output_per_m_tokens} readOnly={!canEdit}
                           onChange={v => setModel(id, { output_per_m_tokens: v })} inputCls={inputCls} />
              </div>
            </div>
          ))}
          {canEdit && (
            <div className="flex items-center gap-2">
              <input type="text" placeholder="Add model id (e.g. claude-opus-4-20250514)"
                     value={newModelId} onChange={e => setNewModelId(e.target.value)}
                     data-testid="rc-new-model-id"
                     className="flex-1 bg-surface-2 border border-border rounded-sm px-2.5 py-1.5 text-[12px] font-mono-tabular focus:border-primary outline-none" />
              <button onClick={addModel} data-testid="rc-new-model-add"
                      className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm">
                Add model
              </button>
            </div>
          )}
        </div>
      </Tile>

      <div className="flex items-center gap-3 pt-1">
        <div className="text-[11px] text-muted" data-testid="rate-card-updated">
          Last update:{" "}
          <strong className="text-fg font-mono-tabular">
            {rc?.updated_at ? rc.updated_at.slice(0, 16).replace("T", " ") : "—"}
          </strong>
          {rc?.updated_by && <> by <strong className="text-fg font-mono-tabular">{rc.updated_by}</strong></>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {saved && <Badge status="success" testid="rate-card-saved">{saved}</Badge>}
          <button onClick={onXlsx} disabled={downloading} data-testid="rate-card-xlsx"
                  className="px-3 py-1.5 text-[11px] border border-success/60 text-success hover:bg-success hover:text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
            {downloading ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
            Excel export
          </button>
          {canEdit && (
            <>
              <button onClick={onReset} disabled={!dirty || saving} data-testid="rate-card-reset"
                      className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm disabled:opacity-40">
                Reset
              </button>
              <button onClick={onSave} disabled={!dirty || saving} data-testid="rate-card-save"
                      className="px-4 py-1.5 text-[11px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                {saving ? "Saving…" : "Save & recost"}
              </button>
            </>
          )}
        </div>
      </div>

      <RateCardHistoryTile
        history={history}
        loading={historyLoading}
        expandedVersion={expandedVersion}
        setExpandedVersion={setExpandedVersion}
        onActivateReplay={onActivateReplay}
        onReload={loadHistory}
      />
    </>
  );
}

function RateCardHistoryTile({ history, loading, expandedVersion, setExpandedVersion, onActivateReplay, onReload }) {
  const rows = history || [];
  return (
    <Tile testid="rate-card-history" title="Rate card history" action={
      <div className="flex items-center gap-2">
        <Badge status="info">{rows.length} version{rows.length === 1 ? "" : "s"}</Badge>
        <button onClick={onReload} data-testid="rate-card-history-reload"
                className="px-2 py-1 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
          <RefreshCcw size={11} /> Reload
        </button>
      </div>
    }>
      {loading && rows.length === 0 ? (
        <div className="py-8 text-center text-muted text-[12px]">
          <Loader2 size={14} className="inline animate-spin mr-1.5" /> Loading history…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-muted text-[12px] italic">
          No rate card edits recorded yet. The next save will appear here.
        </div>
      ) : (
        <div className="space-y-2 max-h-[460px] overflow-y-auto -mx-1 px-1">
          {rows.map(v => {
            const isOpen = expandedVersion === v.version_id;
            return (
              <div key={v.version_id} data-testid={`rate-history-${v.version_id}`}
                   className="border border-border rounded-sm bg-surface-2/40">
                <button onClick={() => setExpandedVersion(isOpen ? null : v.version_id)}
                        data-testid={`rate-history-toggle-${v.version_id}`}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-surface-2/60 transition-colors">
                  <History size={13} className="text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-mono-tabular font-medium">
                      {(v.created_at || "").slice(0, 19).replace("T", " ")} UTC
                    </div>
                    <div className="text-[10.5px] text-muted">
                      by <strong className="font-mono-tabular text-fg">{v.actor || "unknown"}</strong>
                      {" · "}
                      {v.change_count} change{v.change_count === 1 ? "" : "s"}
                      {v.changes?.[0] && (
                        <> &middot; <span className="font-mono-tabular text-fg">{v.changes[0].field}</span></>
                      )}
                      {(v.change_count || 0) > 1 && <> + {v.change_count - 1} more</>}
                    </div>
                  </div>
                  <button data-testid={`rate-history-replay-${v.version_id}`}
                          onClick={e => { e.stopPropagation(); onActivateReplay(v); }}
                          className="shrink-0 px-2.5 py-1 text-[11px] border border-info text-info hover:bg-info hover:text-primary-fg rounded-sm inline-flex items-center gap-1">
                    <PlayCircle size={11} /> Replay in Overview
                  </button>
                  <ChevronRight size={13} className={cn("text-muted shrink-0 transition-transform", isOpen && "rotate-90")} />
                </button>
                {isOpen && (
                  <div className="border-t border-border px-3 py-2.5 bg-bg"
                       data-testid={`rate-history-detail-${v.version_id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5">
                      Changes
                    </div>
                    <table className="w-full text-[11.5px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-muted">
                          <th className="py-1 pr-3 text-left">Field</th>
                          <th className="py-1 pr-3 text-right">From</th>
                          <th className="py-1 pr-3 text-right">To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(v.changes || []).map((ch, i) => (
                          <tr key={i} className="border-t border-border/60">
                            <td className="py-1 pr-3 font-mono-tabular">{ch.field}</td>
                            <td className="py-1 pr-3 text-right font-mono-tabular">{formatRateValue(ch.from)}</td>
                            <td className="py-1 pr-3 text-right font-mono-tabular text-primary font-semibold">{formatRateValue(ch.to)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Tile>
  );
}

function formatRateValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 6 });
  return String(v);
}

function RateInput({ label, unit, value, onChange, readOnly, inputCls, testid }) {
  return (
    <div>
      <label className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">
        <span>{label}</span>
        <span className="text-subtle normal-case tracking-normal">{unit}</span>
      </label>
      <input type="number" step="any" inputMode="decimal"
             data-testid={testid}
             value={value ?? ""}
             onChange={e => onChange(e.target.value)}
             readOnly={readOnly}
             className={inputCls} />
    </div>
  );
}

function SkeletonOverview() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="cost-skeleton">
      <div className="grid grid-cols-12 gap-4">
        {[1, 2, 3].map(i => <div key={i} className="col-span-4 h-32 border border-border bg-surface-2/60 rounded-sm" />)}
      </div>
      <div className="grid grid-cols-12 gap-4">
        {[1, 2, 3].map(i => <div key={i} className="col-span-4 h-40 border border-border bg-surface-2/60 rounded-sm" />)}
      </div>
      <div className="h-64 border border-border bg-surface-2/60 rounded-sm" />
    </div>
  );
}
function SkeletonCard() {
  return <div className="h-64 border border-border bg-surface-2/60 rounded-sm animate-pulse" />;
}
function ErrorBanner({ message, onRetry }) {
  return (
    <div data-testid="cost-error" className="border border-critical/40 bg-critical/5 text-critical rounded-sm p-4">
      <div className="font-semibold text-[12px] mb-1">Could not load cost data</div>
      <div className="text-[11.5px]">{message}</div>
      <button onClick={onRetry} className="mt-2 text-[11px] underline hover:no-underline">Retry →</button>
    </div>
  );
}
function deepClone(x) { return JSON.parse(JSON.stringify(x)); }
